/**
 * Orquestación de lectura del **Motor_Casos** para la vista
 * `PriorityCases` (tarea 2.12). Consume el servicio `MotorCasos` resuelto
 * por el runtime selector (`../api/runtime`) — demo client-side por
 * defecto, conectado detrás del mismo contrato — y produce un modelo de
 * vista enriquecido con:
 *
 *   - la lista de {@link Caso}s con su Score_Caso vigente, **ordenada de
 *     forma descendente** por Score (R3.4);
 *   - la {@link ExplainabilityCaso} con las **nueve contribuciones con
 *     signo** (R4.1);
 *   - la {@link CadenaCausal} del Caso (R4.4);
 *   - las **acciones sugeridas** vinculadas al Registro (R4.7);
 *
 * y un timeline de Eventos_Operacionales por Caso (R4.5), recuperado del
 * Event_Store compartido a través del propio servicio.
 *
 * La evidencia se **deriva** de los JSONs reales de Antofagasta
 * (`dmas.json`, `alarmas.json`) con {@link derivarEvidencias}; no se
 * duplican entidades de la fundación (R17). Toda la lógica de scoring,
 * explainability, orden, cadena causal y acciones vive en el motor; aquí
 * solo se acumulan los deltas de `ingest` y se compone el modelo de vista.
 */
import type {
  AccionSugerida,
  Caso,
  ContribucionExplain,
  EventoOperacional,
  Evidencia,
  MotorCasos,
  ParamsAccion,
  PesosScore,
  TipoEntidad,
  VinculoCausal,
} from "../api/contracts";

import { cadenaCausalDeCaso, PESOS_SCORE_DEFAULT } from "./motorCasos";
import type { GraphData } from "./graph";

/** Versión de código para la paridad determinista del scoring (R3.5). */
export const CODE_VERSION = "ai-operational-layer/motorCasos@2.12";

/** Banda de prioridad derivada del Score_Caso para la UI. */
export type BandaPrioridad = "P1" | "P2" | "P3" | "P4";

/** Modelo de vista de un Caso, enriquecido con la salida del Motor_Casos. */
export interface CasoEnriquecido {
  caso: Caso;
  /** Las nueve contribuciones con signo de la Explainability_Caso (R4.1). */
  contribuciones: ContribucionExplain[];
  /** Acciones sugeridas vinculadas al Registro (R4.7). */
  acciones: AccionSugerida[];
  /** Vínculos de la Cadena_Causal del Caso (R4.4). */
  cadena: VinculoCausal[];
  /** Banda de prioridad derivada del Score_Caso. */
  banda: BandaPrioridad;
}

/** Banda de prioridad según el valor del Score_Caso [0,100]. */
export function bandaDeScore(valor: number): BandaPrioridad {
  if (valor >= 80) return "P1";
  if (valor >= 65) return "P2";
  if (valor >= 50) return "P3";
  return "P4";
}

/**
 * Aplica un {@link CasoDelta} a un mapa de Casos por id, reflejando la
 * creación, actualización o consolidación: registra el Caso resultante y
 * elimina los Casos absorbidos por una consolidación (R3.9).
 */
function aplicarDelta(mapa: Map<string, Caso>, delta: { caso: Caso; consolidados?: string[] }): void {
  if (delta.consolidados) {
    for (const id of delta.consolidados) mapa.delete(id);
  }
  mapa.set(delta.caso.caso_id, delta.caso);
}

/**
 * Ingesta una lista de evidencia en el Motor_Casos (acumulando los
 * deltas) y devuelve los Casos vigentes **enriquecidos y ordenados** por
 * Score_Caso descendente (R3.4). Cada Caso recibe su Score_Caso vigente
 * (R3.3), su Explainability (R4.1), su Cadena_Causal (R4.4) y sus
 * acciones sugeridas (R4.7), todo calculado por el motor.
 *
 * No muta la evidencia de entrada. Es seguro invocarla repetidamente con
 * evidencia adicional sobre el **mismo** motor: el ciclo de vida del
 * Motor_Casos correlaciona/consolida sobre la entidad raíz (R3.1, R3.2).
 */
export async function ingestarYEnriquecer(
  motor: MotorCasos,
  evidencias: Evidencia[],
  pesos: PesosScore = PESOS_SCORE_DEFAULT,
): Promise<CasoEnriquecido[]> {
  const mapa = new Map<string, Caso>();

  for (const ev of evidencias) {
    const res = await motor.ingest(ev);
    if (res.ok) aplicarDelta(mapa, res.value);
  }

  // Calcular el Score_Caso vigente de cada Caso y fijarlo antes de ordenar.
  const conScore: Caso[] = [...mapa.values()].map((caso) => {
    const score = motor.score(caso, pesos, CODE_VERSION);
    return score.ok ? { ...caso, score: score.value } : caso;
  });

  // Orden total determinista por Score_Caso descendente (R3.4).
  const ordenados = motor.order(conScore);

  return ordenados.map((caso) => {
    const explain = motor.explain(caso, pesos);
    const acciones = motor.suggestActions(caso);
    return {
      caso,
      contribuciones: explain.ok ? explain.value.contribuciones : [],
      acciones: acciones.ok ? acciones.value : [],
      cadena: cadenaCausalDeCaso(caso).vinculos,
      banda: bandaDeScore(caso.score?.valor ?? 0),
    };
  });
}

/**
 * Recupera el timeline de Eventos_Operacionales de un Caso ordenado por
 * `ts` no decreciente (R4.5), vía el servicio del Motor_Casos.
 */
export async function timelineDeCaso(
  motor: MotorCasos,
  caso: Caso,
): Promise<EventoOperacional[]> {
  const res = await motor.timeline(caso);
  return res.ok ? res.value : [];
}

/**
 * Construye el `GraphData` (insumo de {@link derivarEvidencias}) a partir
 * del dataset real ya cargado por `../api/realData`. Devuelve `null` si
 * no hay datos reales disponibles.
 */
export function graphDataDeReal(
  real: { dmas: GraphData["dmas"]; gateways?: GraphData["gateways"]; alarmas?: GraphData["alarmas"] } | null,
): GraphData | null {
  if (!real || real.dmas.length === 0) return null;
  return { dmas: real.dmas, gateways: real.gateways, alarmas: real.alarmas };
}

// ---------------------------------------------------------------------------
// Derivación de parámetros para invocar una acción sugerida (R4.7, R6.6)
// ---------------------------------------------------------------------------

/**
 * Mapa tipo-de-entidad → nombre del parámetro de invocación de la
 * Accion_Semantica. Refleja la convención del catálogo
 * (`./actionCatalog`) y del propio Registro (`./registry`): la entidad
 * raíz de un Caso es típicamente una DMA, de modo que la acción se invoca
 * con `{ dma_ref: <id> }`.
 */
const PARAM_POR_TIPO_ENTIDAD: Partial<Record<TipoEntidad, string>> = {
  DMA: "dma_ref",
  Valvula: "valvula_ref",
  PRV: "prv_ref",
  Cuadrilla: "cuadrilla_ref",
  Orden_Inspeccion: "orden_ref",
  Medidor: "device_ref",
  Anomalia: "evidencia_ref",
};

/**
 * Deriva los {@link ParamsAccion} con los que se invoca una acción
 * sugerida a partir de la **entidad raíz del Caso** (R4.7). La acción se
 * referencia contra la entidad raíz (p.ej. `{ dma_ref: caso.entidad_raiz_ref.id }`),
 * que el Registro usa para etiquetar el Evento_Operacional y aplicar el
 * gate de permisos/políticas y HITL antes de cualquier efecto (R6.6).
 *
 * El nombre del parámetro se deriva del tipo de la entidad raíz; si el
 * tipo no tiene un parámetro conocido en el catálogo, se usa `dma_ref`
 * como fallback seguro (coincide con el fallback del Registro).
 */
export function paramsDeAccionCaso(caso: Caso): ParamsAccion {
  const raiz = caso.entidad_raiz_ref;
  const nombre = PARAM_POR_TIPO_ENTIDAD[raiz.tipo] ?? "dma_ref";
  return { [nombre]: raiz.id };
}
